/**
 * @author mschuetz / http://mschuetz.at
 *
 * adapted from THREE.OrbitControls by
 *
 * @author qiao / https://github.com/qiao
 * @author mrdoob / http://mrdoob.com
 * @author alteredq / http://alteredqualia.com/
 * @author WestLangley / http://github.com/WestLangley
 * @author erich666 / http://erichaines.com
 *
 *
 *
 */
// Touched Pythagoras

import * as THREE from 'three';
import { MOUSE } from '../defines';
import { EventDispatcher } from '../EventDispatcher';
import { Utils } from '../utils';
import { Viewer } from '../viewer/viewer';
import { Scene } from '../viewer/Scene';
import * as OrbitEngine from './orbit/OrbitEngine';
import { isTouchpadNavigation } from '../../modules/Properties/integration/UserSettingsIntegration';

/** Shape of the drag data attached to 'drag' events from InputHandler */
interface DragState {
  start: THREE.Vector2;
  end: THREE.Vector2;
  lastDrag: THREE.Vector2;
  mouse: number;
  event?: MouseEvent;
  startHandled?: boolean;
  startView: unknown;
  dragTarget: unknown;
}

interface OrbitDragEvent {
  type: string;
  drag: DragState;
  viewer: Viewer;
}

interface OrbitScrollEvent {
  type: string;
  delta: number;
  deltaX?: number;
  event?: WheelEvent;
  mouse: THREE.Vector2;
  object: unknown;
}

interface OrbitDblClickEvent {
  type: string;
  mouse: THREE.Vector2;
  object: unknown;
}

interface OrbitTouchEvent {
  type: string;
  touches: TouchList;
  changedTouches: TouchList;
}


export class OrbitControls extends EventDispatcher {
  viewer: Viewer;
  renderer: THREE.WebGLRenderer;
  scene: Scene | null;
  sceneControls: THREE.Scene;
  rotationSpeed: number;
  fadeFactor: number;
  /**
   * Single source of truth for orbit deltas. Exposed as discrete getters/
   * setters below for backwards compatibility with the 40 sites that read
   * and mutate `this.yawDelta` etc. directly. The shared OrbitEngine reads
   * and mutates this object.
   */
  deltas: OrbitEngine.OrbitDeltas = OrbitEngine.createDeltas();
  get yawDelta(): number { return this.deltas.yawDelta; }
  set yawDelta(v: number) { this.deltas.yawDelta = v; }
  get pitchDelta(): number { return this.deltas.pitchDelta; }
  set pitchDelta(v: number) { this.deltas.pitchDelta = v; }
  get rollDelta(): number { return this.deltas.rollDelta; }
  set rollDelta(v: number) { this.deltas.rollDelta = v; }
  get panDelta(): THREE.Vector2 { return this.deltas.panDelta; }
  /** Setter exists only because the old field was assignable; in practice
   *  callers mutate `panDelta.x/y` in place, never replace the Vector2. */
  set panDelta(v: THREE.Vector2) { this.deltas.panDelta = v; }
  get radiusDelta(): number { return this.deltas.radiusDelta; }
  set radiusDelta(v: number) { this.deltas.radiusDelta = v; }

  /**
   * One zoom step (the same step a wheel notch performs). `direction` is +1 to zoom out,
   * -1 to zoom in, matching `applyWheelZoom3D`'s `wheelDelta` convention.
   */
  zoomBy(direction: number): void {
    if (!this.scene) return;
    OrbitEngine.applyWheelZoom3D(this.deltas, this.scene.view, direction);
  }

  doubleClickZoomEnabled: boolean;
  tweens: OrbitEngine.TweenInstance[];
  _mode: 'DRAG' | 'PAN' | null = null;
  _forcedMode: 'DRAG' | 'PAN' | null = null;
  enabled = true;

  constructor(viewer: Viewer) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;

    this.scene = null;
    this.sceneControls = new THREE.Scene();

    this.rotationSpeed = 5;

    this.fadeFactor = 20;
    // Delta fields (yaw/pitch/roll/pan/radius) live on `this.deltas`,
    // initialized at declaration. The discrete-field getters/setters above
    // proxy to it so existing callers keep working.

    this.doubleClickZoomEnabled = true;

    this.tweens = [];

    const drag = (e: OrbitDragEvent) => {
      // Skip if controls are disabled
      if (!this.enabled) return;

      // Additional check: skip if controls were disabled during the drag
      if (!this.enabled) return;

      if (e.drag.startHandled === undefined) {
        e.drag.startHandled = true;

        this.dispatchEvent({ type: 'start' });
      }

      const ndrag = {
        x: e.drag.lastDrag.x / this.renderer.domElement.clientWidth,
        y: e.drag.lastDrag.y / this.renderer.domElement.clientHeight,
      };

      const dragMode = this.getDragMode(e);

      if (dragMode === 'DRAG') {
        OrbitEngine.applyDragPan(this.deltas, ndrag.x, ndrag.y);
        this.mode = 'DRAG';
        this.stopTweens();
      } else if (dragMode === 'PAN') {
        OrbitEngine.applyDragOrbit(this.deltas, ndrag.x, ndrag.y, this.rotationSpeed);
        this.mode = 'PAN';
        this.stopTweens();
      }
    };

    const drop = () => {
      this.mode = null;
      this.dispatchEvent({ type: 'end' });
    };

    const scroll = (e: OrbitScrollEvent) => {
      if (!this.scene) return;
      // 3D: scroll zooms. 2D: the Navigation-mode user setting decides —
      // Touchpad (default): scroll pans (side-scroll pans the other axis)
      // and ctrl+scroll (= trackpad pinch) zooms toward the cursor.
      // Mouse: scroll zooms toward the cursor, the classic discrete-wheel
      // convention.
      if (this.viewer.viewerMode.in2DMode) {
        if (isTouchpadNavigation() && !e.event?.ctrlKey) {
          OrbitEngine.applyWheelPan2D(this.deltas, e.delta ?? 0, e.deltaX ?? 0, !!e.event?.shiftKey);
        } else {
          this.zoomTowardsMouse(e.mouse, -e.delta * 0.1);
        }
      } else {
        OrbitEngine.applyWheelZoom3D(this.deltas, this.scene.view, e.delta);
      }
      this.stopTweens();
    };

    const dblclick = (e: OrbitDblClickEvent) => {
      if (this.doubleClickZoomEnabled) {
        this.zoomToLocation(e.mouse);
      }
    };

    let previousTouch: OrbitTouchEvent | null = null;
    const touchStart = (e: OrbitTouchEvent) => {
      previousTouch = e;
    };
    const touchEnd = (e: OrbitTouchEvent) => {
      previousTouch = e;
    };

    const touchMove = (e: OrbitTouchEvent) => {
      if (!this.scene) return;
      if (e.touches.length === 2 && previousTouch && previousTouch.touches.length === 2) {
        const prev = previousTouch;
        const curr = e;

        const prevDX = prev.touches[0].pageX - prev.touches[1].pageX;
        const prevDY = prev.touches[0].pageY - prev.touches[1].pageY;
        const prevDist = Math.sqrt(prevDX * prevDX + prevDY * prevDY);

        const currDX = curr.touches[0].pageX - curr.touches[1].pageX;
        const currDY = curr.touches[0].pageY - curr.touches[1].pageY;
        const currDist = Math.sqrt(currDX * currDX + currDY * currDY);

        const zoomSpeed = 4; // Introduced for Pointorama. Zooming with two fingers was very slow.
        const delta = zoomSpeed * (currDist / prevDist - 1) + 1;
        const resolvedRadius = this.scene.view.radius + this.radiusDelta;
        const newRadius = resolvedRadius / delta;
        this.radiusDelta = newRadius - resolvedRadius;

        this.stopTweens();
      } else if (e.touches.length === 3 && previousTouch && previousTouch.touches.length === 3) {
        const prev = previousTouch;
        const curr = e;

        const prevMeanX = (prev.touches[0].pageX + prev.touches[1].pageX + prev.touches[2].pageX) / 3;
        const prevMeanY = (prev.touches[0].pageY + prev.touches[1].pageY + prev.touches[2].pageY) / 3;

        const currMeanX = (curr.touches[0].pageX + curr.touches[1].pageX + curr.touches[2].pageX) / 3;
        const currMeanY = (curr.touches[0].pageY + curr.touches[1].pageY + curr.touches[2].pageY) / 3;

        const delta = {
          x: (currMeanX - prevMeanX) / this.renderer.domElement.clientWidth,
          y: (currMeanY - prevMeanY) / this.renderer.domElement.clientHeight,
        };

        this.panDelta.x += delta.x;
        this.panDelta.y += delta.y;

        this.stopTweens();
      }

      previousTouch = e;
    };

    this.addEventListener('touchstart', touchStart);
    this.addEventListener('touchend', touchEnd);
    this.addEventListener('touchmove', touchMove);
    this.addEventListener('drag', drag);
    this.addEventListener('drop', drop);
    this.addEventListener('mousewheel', scroll);
    this.addEventListener('dblclick', dblclick);
  }

  private getDragMode(e: OrbitDragEvent): 'DRAG' | 'PAN' | null {
    const { mouse, event } = e.drag;
    const in2D = this.viewer.viewerMode.in2DMode;
    const ctrl = event?.ctrlKey;
    const shift = event?.shiftKey;

    // Forced mode overrides default bindings (but orbit is blocked in 2D)
    if (this.forcedMode) {
      if (!(this.forcedMode === 'PAN' && in2D)) return this.forcedMode;
    }

    if (in2D) {
      // 2D: middle mouse, ctrl+left, ctrl+right, shift+right → pan. No orbit.
      const isPan = mouse === MOUSE.MIDDLE || (mouse === MOUSE.LEFT && ctrl) || (mouse === MOUSE.RIGHT && ctrl) || (mouse === MOUSE.RIGHT && shift);
      return isPan ? 'DRAG' : null;
    }

    // 3D: right/middle → pan, left → orbit
    if (mouse === MOUSE.RIGHT || mouse === MOUSE.MIDDLE) return 'DRAG';
    if (mouse === MOUSE.LEFT) return 'PAN';

    return null;
  }

  private setCursor(mode: 'DRAG' | 'PAN' | null) {
    if (mode === 'DRAG') {
      Utils.changeCursor('move');
    } else if (mode === 'PAN') {
      Utils.changeCursor('orbit');
    } else {
      Utils.changeCursor('auto');
    }
  }

  get mode() {
    return this._mode;
  }

  set mode(mode) {
    this._mode = mode;
    this.setCursor(mode);
  }

  get forcedMode() {
    return this._forcedMode;
  }

  set forcedMode(mode) {
    this._forcedMode = mode;
  }

  setScene(scene: Scene) {
    this.scene = scene;
  }

  stop() {
    this.yawDelta = 0;
    this.pitchDelta = 0;
    this.rollDelta = 0;
    this.radiusDelta = 0;
    this.panDelta.set(0, 0);
  }

  zoomTowardsMouse(mouse: THREE.Vector2, zoomFactor: number) {
    if (!this.scene) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    OrbitEngine.zoomTowardsMouse(this.scene.view, this.scene.getActiveCamera(), mouse, rect.width, rect.height, zoomFactor, this.deltas);
    this.stopTweens();
  }

  zoomToLocation(mouse: THREE.Vector2) {
    if (!this.scene) return;
    const scene = this.scene;
    const camera = scene.getActiveCamera();

    // Picking lives on the adapter — Utils.getMousePointCloudIntersection
    // uses renderer.domElement.clientWidth/Height for normalization (i.e.
    // the main canvas size), which is what we want here.
    const I = Utils.getMousePointCloudIntersection(mouse, camera, this.viewer, scene.pointclouds, {
      pickClipped: false,
    });
    if (I === null || !('location' in I)) return;

    // Compute the target radius (clamp by visible-node bounding sphere on
    // the picked ray, with a minimum jump distance).
    const minimumJumpDistance = 0.2;
    const domElement = this.renderer.domElement;
    const ray = Utils.mouseToRay(mouse, camera, domElement.clientWidth, domElement.clientHeight);
    const visibleNodes = I.pointcloud.visibleNodes ?? [];
    const nodes = I.pointcloud.nodesOnRay?.(visibleNodes, ray) ?? [];
    const lastNode = nodes[nodes.length - 1] as { getBoundingSphere(sphere: THREE.Sphere): THREE.Sphere } | undefined;
    const nodeRadius = lastNode?.getBoundingSphere(new THREE.Sphere()).radius ?? scene.view.radius;
    let targetRadius = Math.min(scene.view.radius, nodeRadius);
    targetRadius = Math.max(minimumJumpDistance, targetRadius);

    // Camera position so it sits `targetRadius` from the picked location,
    // along the current view direction. (`d` is the inverted look dir.)
    const d = scene.view.direction.multiplyScalar(-1);
    const cameraTargetPosition = new THREE.Vector3().addVectors(I.location, d.multiplyScalar(targetRadius));

    OrbitEngine.zoomToLocation({
      view: scene.view,
      targetPosition: cameraTargetPosition,
      // Original code computed `cameraTargetPosition.distanceTo(I.location)`
      // here, which is mathematically identical to `targetRadius` since
      // cameraTargetPosition is constructed `targetRadius` away from
      // I.location along a unit direction. Pass the value we already have.
      targetRadius,
      durationMs: 600,
      onTick: (v) => this.viewer.setMoveSpeed(v.radius),
      tweens: this.tweens,
    });
  }

  stopTweens() {
    OrbitEngine.stopTweens(this.tweens);
  }

  update(delta: number) {
    if (!this.scene) return;
    const scene = this.scene;
    const inMode2D = this.viewer.viewerMode.in2DMode;

    OrbitEngine.applyDeltas(scene.view, this.deltas, scene.getActiveCamera(), delta, { allowYaw: !inMode2D, allowPitch: !inMode2D }, { fadeFactor: this.fadeFactor, rotationSpeed: this.rotationSpeed });

    // Side effect retained from the inline implementation: keep moveSpeed
    // tracking the current orbit radius so other tools (e.g. FPV controls)
    // pick up sane sensitivity.
    this.viewer.setMoveSpeed(scene.view.radius);
  }
}
